import React, { useEffect, useRef } from 'react';
import { setupCharacter } from './utils/renderCharacter.ts';
import {
  AnimationCharacter,
  AssetType,
  CharacterRef,
  Layer,
  Position,
} from '../../types/typing.ts';
import { Application } from 'pixi.js';
import { updateCircleVisibility } from './utils/recordAnimation.ts';
import { isBackgroundRecordingApp } from './utils/backgroundRecordingState.ts';

interface CharacterRendererProps {
  app: Application | null;
  charactersRef: React.MutableRefObject<Map<string, CharacterRef>>;
  animation: AnimationCharacter[] | undefined;
  width: number;
  height: number;
  layers: Layer[] | undefined;
  onUpdateCharacter: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
  isRecording: boolean;
  recordingStartTime: number | null;
  onAssetSelection: (id: string, assetName: AssetType) => void;
  selectedMenu: string | null | undefined;
  setLastSelectedCharacterIndex: (id: string) => void;
}

const CharacterRenderer: React.FC<CharacterRendererProps> = ({
  app,
  charactersRef,
  animation,
  width,
  height,
  onUpdateCharacter,
  isRecording,
  recordingStartTime,
  onAssetSelection,
  layers,
  selectedMenu,
  setLastSelectedCharacterIndex,
}) => {
  const selectedMenuRef = useRef<string | null | undefined>(null);
  const isRecordingRef = useRef<boolean | null | undefined>(null);
  const recordingStartTimeRef = useRef<number | null | undefined>(null);

  // Update the ref whenever selectedMenu changes
  useEffect(() => {
    selectedMenuRef.current = selectedMenu;
  }, [selectedMenu]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    recordingStartTimeRef.current = recordingStartTime;
  }, [recordingStartTime]);

  useEffect(() => {
    if (!animation || !app) return;
    const setupAllCharacters = async () => {
      // Create/update characters
      for (let index = 0; index < animation.length; index++) {
        const char = animation[index];
        if (!charactersRef.current.has(char.id)) {
          await setupCharacter(
            char,
            app,
            charactersRef,
            onUpdateCharacter,
            isRecordingRef,
            recordingStartTimeRef,
            onAssetSelection,
            selectedMenuRef,
            setLastSelectedCharacterIndex
          );
        } else {
          // Update existing character properties
          const existingChar = charactersRef.current.get(char.id);
          if (existingChar) {
            existingChar.sprite?.position.set(
              char.position?.x || width / 2,
              char.position?.y || height / 2
            );
            existingChar.sprite?.scale.set(char.scale || 1);
          }
        }
      }

      // Cleanup removed characters
      const animationIds = new Set(animation.map((item) => item.id));

      const keysToRemove = [...charactersRef.current.keys()].filter(
        (key) => !animationIds.has(key)
      );

      keysToRemove.forEach((key) => {
        const char = charactersRef.current.get(key);
        if (char) {
          char.destroy?.();
          charactersRef.current.delete(key);
        }
      });
    };
    setupAllCharacters();
  }, [
    animation,
    width,
    height,
    app,
    onUpdateCharacter,
    isRecording,
    recordingStartTime,
  ]);

  useEffect(() => {
    if (!app) return;

    return () => {
      if (isBackgroundRecordingApp(app)) {
        return;
      }

      charactersRef.current.forEach((char) => {
        char.destroy?.();
      });
      charactersRef.current.clear();
    };
  }, [app, charactersRef]);

  useEffect(() => {
    charactersRef.current?.forEach((charRef) => {
      updateCircleVisibility(charRef.pathCircles, selectedMenuRef);
    });
  }, [selectedMenu]);

  useEffect(() => {
    if (layers) {
      charactersRef.current?.forEach((charRef) => {
        const matchingLayer = layers.find((layer) => layer.id === charRef.id);
        if (matchingLayer) {
          charRef.sprite.zIndex = layers.indexOf(matchingLayer);
          app?.stage.sortChildren();
        }
      });
    }
  }, [layers]);

  return null;
};

export default CharacterRenderer;
